import { all, takeLatest, select, put } from "redux-saga/effects";
import {
  CREATE_PRODUCT_PLAN,
  DELETE_ORDER,
  GET_ACTIVE_PRODUCT_PLAN,
  GET_DENTISTS,
  GET_INSURERS,
  GET_PATIENT,
  UPDATE_PRODUCT_PLAN,
} from "containers/OrderTreatment/constants";
import {
  createProductPlanError,
  createProductPlanSuccess,
  deleteOrderError,
  deleteOrderSuccess,
  getActiveProductPlanError,
  getActiveProductPlanSuccess,
  getDentistsError,
  getDentistsSuccess,
  getInsurersError,
  getInsurersSuccess,
  getPatientError,
  getPatientSuccess,
  setActiveOrderId,
  updateProductPlanError,
  updateProductPlanSuccess,
} from "containers/OrderTreatment/actions";
import { makeSelectPractice } from "containers/PracticePage/selectors";
import { authorizedRequest, HTTP_METHODS } from "utils/request";
import {
  makeSelectOrderTreatmentActiveOrderId,
  makeSelectOrderTreatmentPatient,
} from "containers/OrderTreatment/selectors";
import {
  getProductTypesError,
  getProductTypesSuccess,
} from "containers/TreatmentFlow/actions";

function* getPatientSaga({ id }) {
  try {
    const practice = yield select(makeSelectPractice());
    const response = yield authorizedRequest(
      `/practices/${practice.id}/patients/${id}`,
    );
    yield put(getPatientSuccess(response));
  } catch (error) {
    yield put(getPatientError(error));
  }
}

function* getEndProductTypesSaga() {
  try {
    const response = yield authorizedRequest("/ProductTypes");
    yield put(getProductTypesSuccess(response));
  } catch (error) {
    yield put(getProductTypesError(error));
  }
}

function* getInsurersSaga() {
  try {
    const response = yield authorizedRequest("/insurers");
    yield put(getInsurersSuccess(response));
  } catch (error) {
    yield put(getInsurersError(error));
  }
}

function* getDentistsSaga() {
  try {
    const practice = yield select(makeSelectPractice());
    const response = yield authorizedRequest(
      `/practices/${practice.id}/dentists`,
    );
    yield put(getDentistsSuccess(response));
  } catch (error) {
    yield put(getDentistsError(error));
  }
}

function* createProductPlanSaga({ productPlan }) {
  try {
    const practice = yield select(makeSelectPractice());
    const patient = yield select(makeSelectOrderTreatmentPatient());
    const response = yield authorizedRequest(
      `/practices/${practice.id}/patient/productPlans`,
      {
        method: HTTP_METHODS.POST,
        body: {
          ...productPlan,
          patientId: patient.id,
        },
      },
    );

    yield put(createProductPlanSuccess(response));
  } catch (error) {
    yield put(createProductPlanError(error));
  }
}

function* updateProductPlanSaga({ productPlan }) {
  try {
    const practice = yield select(makeSelectPractice());
    const response = yield authorizedRequest(
      `/practices/${practice.id}/patient/productPlans`,
      {
        method: HTTP_METHODS.PUT,
        body: {
          id: productPlan.id,
          patientId: productPlan.patientId,
          productTypeConfigurationId: productPlan.productTypeConfigurationId,
        },
      },
    );

    yield put(updateProductPlanSuccess(response));
  } catch (error) {
    yield put(updateProductPlanError(error));
  }
}

function* getActiveProductPlanSaga({ patientId, overrideActiveOrderId = 0 }) {
  try {
    const practice = yield select(makeSelectPractice());
    const response = yield authorizedRequest(
      `/practices/${practice.id}/patient/${patientId}/productPlans/active`,
    );
    yield put(getActiveProductPlanSuccess(response));

    const { orders = [] } = response;

    const lastPlannedOrder =
      orders
        .slice()
        .reverse()
        .find(x => x.status > 1) || {};
    const lastPlannedOrderNumber = lastPlannedOrder.order || 0;

    let orderId = (
      orders
        .filter(x => x.order > lastPlannedOrderNumber)
        .find(
          ({ active, status, disabled }) => active && status === 1 && !disabled,
        ) || {}
    ).order;

    if (overrideActiveOrderId > 0) {
      const overrideOrder = orders.find(
        ({ id }) => id === overrideActiveOrderId,
      );
      if (overrideOrder && overrideOrder.order) {
        orderId = overrideOrder.order;
      }
    }

    yield put(setActiveOrderId(orderId));
  } catch (error) {
    yield put(getActiveProductPlanError(error));
  }
}

function* deleteOrderSaga({ id }) {
  try {
    const practice = yield select(makeSelectPractice());
    const response = yield authorizedRequest(
      `/practices/${practice.id}/productPlan/productOrder/${id}`,
      {
        method: HTTP_METHODS.DELETE,
      },
    );
    yield put(deleteOrderSuccess(response));

    const { orders = [] } = response;
    const orderId = (
      orders.find(({ active, status }) => active && status === 1) || {}
    ).order;

    yield put(setActiveOrderId(orderId));
  } catch (error) {
    yield put(deleteOrderError(error));
  }
}
// Individual exports for testing
export default function* orderTreatmentSaga() {
  yield all([
    takeLatest(GET_DENTISTS, getDentistsSaga),
    takeLatest(GET_PATIENT, getPatientSaga),
    takeLatest(GET_INSURERS, getInsurersSaga),
    takeLatest(CREATE_PRODUCT_PLAN, createProductPlanSaga),
    takeLatest(UPDATE_PRODUCT_PLAN, updateProductPlanSaga),
    takeLatest(GET_ACTIVE_PRODUCT_PLAN, getActiveProductPlanSaga),
    takeLatest(DELETE_ORDER, deleteOrderSaga),
  ]);
  // See example in containers/HomePage/saga.js
}
